import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import LeadModal from "../components/LeadModal";

const VIDEO_EMBED_SRC =
  "https://player.mediadelivery.net/embed/738497/a62d335e-4f92-47b3-ac1b-fb7fac723402?autoplay=true&loop=true&muted=true&preload=true&responsive=true&controls=false";

const LOGO_SRC =
  "https://customer-assets.emergentagent.com/job_dark-reel-studio-1/artifacts/1zbmy37g_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png";

const HEADLINE_LINES = [
  ["Professional", "Video."],
  ["Creative", "Thinking."],
];

export default function Billboard() {
  const [modalOpen, setModalOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <main
      data-testid="billboard-page"
      className="relative h-screen w-screen overflow-hidden bg-black text-white"
    >
      {/* Video layer (Bunny Stream iframe — covers viewport, no chrome) */}
      <div
        className={`absolute inset-0 overflow-hidden transition-opacity duration-[1600ms] ease-out ${
          mounted ? "opacity-100" : "opacity-0"
        }`}
        data-testid="billboard-video"
      >
        <iframe
          src={VIDEO_EMBED_SRC}
          title="Whiplash reel"
          loading="eager"
          allow="autoplay; encrypted-media; picture-in-picture"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border-0 pointer-events-none"
          style={{
            width: "max(100vw, calc(100vh * 16 / 9))",
            height: "max(100vh, calc(100vw * 9 / 16))",
          }}
        />
        {/* Bottom mask — hides Bunny's default player control bar */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black via-black to-transparent pointer-events-none" />
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-black/45 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black/90 pointer-events-none" />
      <div className="absolute inset-0 grain pointer-events-none opacity-40" />

      {/* Top-left: Logo */}
      <div
        className={`absolute top-8 left-8 md:top-10 md:left-12 z-10 transition-all duration-1000 ${
          mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
        }`}
      >
        <img
          src={LOGO_SRC}
          alt="Whiplash Film & Creative"
          className="h-10 md:h-12 w-auto object-contain"
          data-testid="billboard-logo"
        />
      </div>

      {/* Top-right: SHOWREEL micro-detail */}
      <div
        className={`absolute top-8 right-8 md:top-12 md:right-12 z-10 flex items-center gap-3 transition-all duration-1000 delay-200 ${
          mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
        }`}
        data-testid="showreel-tag"
      >
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#d26f28] opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#d26f28]" />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/90">
          Showreel 2026
        </span>
      </div>

      {/* Bottom-left: Headline + CTA */}
      <div className="absolute inset-x-0 bottom-0 z-10 px-8 pb-14 md:px-14 md:pb-20 lg:pb-24">
        <div className="max-w-[1400px] mx-auto">
          <h1
            data-testid="billboard-headline"
            className="font-display uppercase text-white leading-[0.9] tracking-tighter text-[13vw] sm:text-[10vw] md:text-[8.5vw] lg:text-[7.5vw]"
            aria-label="Professional Video. Creative Thinking."
          >
            {HEADLINE_LINES.map((line, lineIdx) => {
              const offset = HEADLINE_LINES.slice(0, lineIdx).flat().length;
              return (
                <span key={lineIdx} className="block">
                  {line.map((word, i) => {
                    const globalIndex = offset + i;
                    const isAccent = word === "Creative";
                    return (
                      <span
                        key={i}
                        className="inline-block overflow-hidden align-top pr-[0.15em]"
                      >
                        <span
                          className={`relative inline-block will-change-transform transition-all duration-[1100ms] ease-[cubic-bezier(0.2,0.7,0.15,1)] ${
                            mounted
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 translate-y-[110%]"
                          } ${
                            isAccent
                              ? "after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-[0.12em] after:w-full after:bg-[#d26f28]"
                              : ""
                          }`}
                          style={{
                            transitionDelay: `${350 + globalIndex * 90}ms`,
                          }}
                        >
                          {word}
                        </span>
                      </span>
                    );
                  })}
                </span>
              );
            })}
          </h1>

          <div
            className={`mt-10 md:mt-14 flex flex-wrap items-center gap-6 transition-all duration-1000 ${
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
            }`}
            style={{ transitionDelay: "1250ms" }}
          >
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              data-testid="cta-work-with-us"
              className="group relative inline-flex items-center gap-4 pl-8 pr-4 py-4 bg-white text-black overflow-hidden"
            >
              <span className="relative z-10 font-mono text-[11px] uppercase tracking-[0.3em] transition-colors duration-500 group-hover:text-white">
                Work With Us
              </span>
              <span className="relative z-10 flex h-9 w-9 items-center justify-center bg-[#d26f28] text-black transition-transform duration-500 group-hover:translate-x-1">
                <ArrowUpRight size={16} />
              </span>
              <span className="absolute inset-0 bg-black translate-y-full transition-transform duration-500 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:translate-y-0" />
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 max-w-xs">
              Brief us on a project · reply within one business day
            </p>
          </div>
        </div>
      </div>

      {/* Bottom-right: Studios + hairline meta */}
      <div
        className={`absolute bottom-6 right-6 md:right-12 z-10 flex flex-col items-end gap-4 transition-opacity duration-1000 delay-[1500ms] ${
          mounted ? "opacity-100" : "opacity-0"
        }`}
        data-testid="studios-block"
      >
        <div className="flex flex-col sm:flex-row items-end sm:items-start gap-4 sm:gap-10 text-right sm:text-left border-t border-white/10 pt-4 pr-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/40 mr-2 sm:mr-0">
            [ Studios ]
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-10">
            <StudioLine
              city="Dallas / Fort Worth"
              phoneDisplay="972 · 863 · 0900"
              phoneHref="+19728630900"
              testId="studio-dfw"
            />
            <StudioLine
              city="Minneapolis"
              phoneDisplay="612 · 564 · 1631"
              phoneHref="+16125641631"
              testId="studio-mpls"
            />
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">
          © {new Date().getFullYear()} · Whiplash Creative
        </span>
      </div>

      <LeadModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </main>
  );
}

function StudioLine({ city, phoneDisplay, phoneHref, testId }) {
  return (
    <a
      href={`tel:${phoneHref}`}
      data-testid={testId}
      className="group inline-flex flex-col items-end sm:items-start"
    >
      <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/50 group-hover:text-white/80 transition-colors">
        {city}
      </span>
      <span className="mt-1 font-display text-sm md:text-base uppercase tracking-wider text-white/90 group-hover:text-white transition-colors flex items-center gap-2">
        <span className="h-1 w-1 rounded-full bg-[#d26f28] group-hover:scale-125 transition-transform" />
        {phoneDisplay}
      </span>
    </a>
  );
}
